import { Head } from "@inertiajs/react";
import { Download, Gift } from "lucide-react";
import SchoolLayout from "@/components/schools/SchoolLayout";
import MilestonesCard from "@/components/schools/MilestonesCard";
import PrizeTrackerCard from "@/components/schools/PrizeTrackerCard";
import ShareDonationPageCard from "@/components/schools/ShareDonationPageCard";
import { TopStudentsCard, TopClassroomsCard } from "@/components/schools/CampaignLeaderboards";
import ClassroomTodayCard from "@/components/schools/classroom/ClassroomTodayCard";
import ClassroomSwitcher from "@/components/schools/classroom/ClassroomSwitcher";
import ClassroomLeaderboardCard from "@/components/schools/classroom/ClassroomLeaderboardCard";
import ClassroomActivityCard from "@/components/schools/classroom/ClassroomActivityCard";
import SchoolSummaryCard from "@/components/schools/classroom/SchoolSummaryCard";
import { Button } from "@/components/ui/button";
import { Notice } from "@/components/ui/notice";
import { campaignPhase, phaseAcceptsMinutes } from "@/lib/campaignPhase";
import { formatRoom } from "@/lib/classroom";
import { pluralize } from "@/lib/format";

/**
 * The teacher's dashboard: their class first — today's numbers and Log
 * minutes, prizes to hand out, the leaderboard and recent activity —
 * then how the whole school is doing. Teachers never need to leave it.
 */
export default function ClassroomDashboard({
  school,
  membership,
  classroom,
  campaign,
  teacher_classrooms = [],
  classroom_milestones = [],
  classroom_milestones_path,
  student_prizes = [],
  students,
  activity = [],
  log_minutes_path,
  take_home_sheets_path,
  congratulations_certificates_path,
}) {
  const phase = campaignPhase(campaign);
  const finished = phase === "finished";
  const running = campaign.state === "active" || campaign.state === "grace";
  const prizesToHandOut = countPrizesToHandOut(student_prizes);
  const hasStudents = classroom.student_count > 0;

  return (
    <SchoolLayout
      school={school}
      membership={membership}
      eyebrow={campaign.name}
      title={formatRoom(classroom.name)}
      actions={
        <>
          {take_home_sheets_path && hasStudents && !finished && (
            <Button variant="outline" size="sm" asChild>
              <a href={take_home_sheets_path}>
                <Download className="h-4 w-4" />
                Take-home sheets
              </a>
            </Button>
          )}
          {congratulations_certificates_path && hasStudents && (finished || phase === "wrapping_up") && (
            <Button variant="outline" size="sm" asChild>
              <a href={congratulations_certificates_path}>
                <Download className="h-4 w-4" />
                Certificates
              </a>
            </Button>
          )}
        </>
      }
    >
      <Head title={`${formatRoom(classroom.name)} · ${school.name}`} />

      <div className="space-y-8">
        <ClassroomSwitcher classrooms={teacher_classrooms} currentId={classroom.id} />

        <ClassroomTodayCard
          classroom={classroom}
          campaign={campaign}
          phase={phase}
          log_minutes_path={log_minutes_path}
          goals_path={classroom_milestones_path}
        />

        {prizesToHandOut > 0 && (
          <Notice
            tone="info"
            icon={Gift}
            action={<a href="#prizes" className="text-sm font-medium text-primary hover:underline">See who</a>}
          >
            {pluralize(prizesToHandOut, "prize")} ready to hand out.
          </Notice>
        )}

        <div className="grid gap-6 lg:grid-cols-[1.6fr_1fr] [&>*]:min-w-0">
          <ClassroomLeaderboardCard
            students={students}
            log_minutes_path={phaseAcceptsMinutes(phase) && campaign.accepting_minutes ? log_minutes_path : null}
          />
          <ClassroomActivityCard activity={activity} editable={running} />
        </div>

        <div id="prizes" className="scroll-mt-6">
          <PrizeTrackerCard prizes={student_prizes} />
        </div>

        <MilestonesCard
          milestones={classroom_milestones}
          manage_path={finished ? null : classroom_milestones_path}
          title="Classroom rewards"
          emptyDescription="Rewards your class earns together, like a movie afternoon at 5,000 minutes read."
          unlockedSummary={({ unlocked, total }) => `${unlocked} of ${total} unlocked by your class.`}
        />

        {campaign.accepting_donations && !finished && (
          <div className="min-w-0">
            <ShareDonationPageCard
              url={school.public_url}
              tvDisplayUrl={school.tv_display_url}
              schoolName={school.name}
              variant="neutral"
            />
          </div>
        )}

        <section className="space-y-6" aria-labelledby="school-wide-heading">
          <h2 id="school-wide-heading" className="font-sans text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            Across the school
          </h2>
          <SchoolSummaryCard school={school} campaign={campaign} phase={phase} />
          <MilestonesCard milestones={campaign.milestones || []} title="School rewards" />
          {((campaign.top_students?.by_money?.length > 0) || (campaign.top_classrooms?.by_money?.length > 0)) && (
            <div className="grid gap-6 lg:grid-cols-2 [&>*]:min-w-0">
              <TopStudentsCard students={campaign.top_students} />
              <TopClassroomsCard classrooms={campaign.top_classrooms} />
            </div>
          )}
        </section>
      </div>
    </SchoolLayout>
  );
}

// Prizes a student has earned but not been handed yet. Only drawn
// winners count for prize draws.
function countPrizesToHandOut(groups) {
  return groups.reduce(
    (sum, group) =>
      sum + group.achievements.filter((a) => !a.disbursed && (group.award_style !== "raffle" || a.won)).length,
    0
  );
}
